import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Wrench, FileCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCreateLead, useConvertLead } from '@/hooks/useLeads';
import { useTechs } from '@/hooks/useReferenceData';
import { supabase } from '@/integrations/supabase/client';
import { createLeadFormSchema, type LeadFormData } from '@/schemas/lead.schema';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LeadForm, type LinkedCustomer } from '@/components/leads/LeadForm';
import { Layout } from '@/components/Layout';
import { EstimateFromLeadDialog } from '@/components/estimates/EstimateFromLeadDialog';

export default function CreateLead() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Prefill from the softphone incoming-call pop (router state) or a
  // ?phone= deep link.
  const prefill = (location.state as { prefill?: { customer_phone?: string; customer_name?: string } } | null)?.prefill;
  const phoneParam = new URLSearchParams(location.search).get('phone');
  const createLead = useCreateLead();
  const convertLead = useConvertLead();
  const { data: techs } = useTechs();

  const [showTechSelector, setShowTechSelector] = useState(false);
  const [selectedTech, setSelectedTech] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  // Existing customer the CSR attached via the picker; travels as customer_id
  // on insert so the lead lands on that customer's card.
  const [linkedCustomer, setLinkedCustomer] = useState<LinkedCustomer | null>(null);
  const [savedLeadForEstimate, setSavedLeadForEstimate] = useState<{
    id: string;
    customer_name: string;
    customer_phone: string | null;
    customer_email: string | null;
    customer_address: string | null;
    issue_description: string;
    quoted_price: number | null;
    customer_id: string | null;
  } | null>(null);

  const { locale } = useCompanyLocale();
  const leadSchema = useMemo(() => createLeadFormSchema(locale), [locale]);

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      customer_type: 'individual',
      customer_name: prefill?.customer_name || '',
      customer_phone: prefill?.customer_phone || phoneParam || '',
      customer_email: '',
      customer_address: '',
      issue_description: '',
      lead_source_id: '',
      campaign_id: '',
      notes: '',
    },
  });

  const handleSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (!user) return;
    setIsProcessing(true);

    const data = form.getValues();
    createLead.mutate(
      { data, userId: user.id, customerId: linkedCustomer?.id ?? null },
      {
        onSuccess: () => {
          toast({
            title: 'Lead created',
            description: 'The lead has been successfully created.',
          });
          navigate('/leads');
        },
        onError: () => {
          setIsProcessing(false);
        },
      }
    );
  };

  const handleConvert = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (!selectedTech) {
      setShowTechSelector(true);
      toast({
        title: 'Technician required',
        description: 'Please select a technician to convert this lead to a job.',
        variant: 'destructive',
      });
      return;
    }

    if (!user) return;
    setIsProcessing(true);

    const data = form.getValues();
    createLead.mutate(
      { data, userId: user.id, customerId: linkedCustomer?.id ?? null },
      {
        onSuccess: (_, variables) => {
          supabase
            .from('leads')
            .select('id')
            .eq('customer_phone', variables.data.customer_phone)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()
            .then(({ data: lead }) => {
              if (lead) {
                convertLead.mutate(
                  { leadId: lead.id, techId: selectedTech },
                  {
                    onSuccess: () => {
                      toast({
                        title: 'Lead converted to job',
                        description: 'The lead has been successfully converted to a job.',
                      });
                      navigate('/jobs');
                    },
                    onError: () => {
                      setIsProcessing(false);
                    },
                  }
                );
              }
            });
        },
        onError: () => {
          setIsProcessing(false);
        },
      }
    );
  };

  const handleCreateEstimate = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const data = form.getValues();

    if (!user) return;
    setIsProcessing(true);

    // The lead is created first; the estimate form opens for that lead only.
    createLead.mutate(
      { data, userId: user.id, customerId: linkedCustomer?.id ?? null },
      {
        onSuccess: async (created) => {
          try {
            const { data: newLead } = await supabase
              .from('leads')
              .select('id, customer_id')
              .eq('id', created.id)
              .maybeSingle();

            setIsProcessing(false);
            if (newLead) {
              setSavedLeadForEstimate({
                id: newLead.id,
                customer_name: data.customer_name,
                customer_phone: data.customer_phone,
                customer_email: data.customer_email || null,
                customer_address: data.customer_address || null,
                issue_description: data.issue_description,
                quoted_price: data.quoted_price ? parseFloat(data.quoted_price) : null,
                customer_id: newLead.customer_id || null,
              });
              setShowEstimateDialog(true);
            }
          } catch {
            setIsProcessing(false);
          }
        },
        onError: () => {
          setIsProcessing(false);
        },
      }
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/leads')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Create New Lead</h1>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Lead Information</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form className="space-y-6">
                <LeadForm form={form} linkedCustomer={linkedCustomer} onLinkCustomer={setLinkedCustomer} />

                {/* Tech Selector - only shown when converting */}
                {showTechSelector && (
                  <div className="p-4 border rounded-md bg-muted/30">
                    <Label>Select Technician *</Label>
                    <Select value={selectedTech} onValueChange={setSelectedTech}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Choose a technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {techs?.map((tech) => (
                          <SelectItem key={tech.id} value={tech.id}>
                            {tech.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-4 sticky bottom-0 bg-background p-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/leads')}
            disabled={isProcessing}
          >
            Cancel
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCreateEstimate}
              disabled={isProcessing}
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Create Estimate
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleSave}
              disabled={isProcessing}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Lead
            </Button>

            <Button
              type="button"
              onClick={handleConvert}
              disabled={isProcessing}
            >
              <Wrench className="h-4 w-4 mr-2" />
              Book Job
            </Button>
          </div>
        </div>

        {/* Estimate Dialog */}
        {savedLeadForEstimate && (
          <EstimateFromLeadDialog
            open={showEstimateDialog}
            onOpenChange={(open) => {
              setShowEstimateDialog(open);
              if (!open) {
                // Navigate to leads after closing the dialog
                navigate('/leads');
              }
            }}
            lead={savedLeadForEstimate}
          />
        )}
      </div>
    </Layout>
  );
}
